import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FeatherMoreHorizontal, FeatherLink, FeatherCopy, FeatherCheck } from '@subframe/core';
import { useAuthStore } from '../store/authStore';
import { formatAddress } from '../utils/formatters';
import { getLinkedWallets, switchWallet as apiSwitchWallet } from '../api/auth';
import { useNotificationStore } from '../store/notificationStore';
import { setSuppressLogoutOn401 } from '../api/client';
import type { LinkedWallet } from '../api/auth';

interface WalletMenuProps {
  onConnectWallet: () => void;
}

export const WalletMenu: React.FC<WalletMenuProps> = ({ onConnectWallet }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const {
    user,
    linkedWallets,
    setLinkedWallets,
    switchToWallet,
    storeWalletToken,
    activeWalletAddress,
  } = useAuthStore();
  const { error: showError, success: showSuccess } = useNotificationStore();

  // Fetch linked wallets when menu opens (refresh in background to catch newly linked wallets)
  useEffect(() => {
    if (isOpen) {
      // Fetch in background without showing loading state (avoids flicker)
      getLinkedWallets()
        .then((wallets) => {
          setLinkedWallets(wallets);
        })
        .catch((err) => {
          console.error('Failed to fetch linked wallets:', err);
        });
    }
  }, [isOpen, setLinkedWallets]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  // Filter out current wallet - use user.xrpl_address as the source of truth
  const currentWalletAddress = user?.xrpl_address;
  const otherWallets = linkedWallets.filter(
    (w) => w.xrpl_address !== currentWalletAddress
  );

  const handleSwitchWallet = useCallback(async (wallet: LinkedWallet) => {
    setIsSwitching(true);

    // Suppress 401 logout handling during wallet switch to prevent race conditions
    // where in-flight requests with old tokens trigger unwanted logout
    setSuppressLogoutOn401(true);

    try {
      // Try to switch locally first (if we have a stored token)
      const switchedLocally = switchToWallet(wallet.xrpl_address);

      if (switchedLocally) {
        showSuccess(`Switched to ${wallet.nickname || formatAddress(wallet.xrpl_address)}`);
        setIsOpen(false);
        // Reload the page to refresh all data
        window.location.reload();
        return;
      }

      // If no local token, get a new one from the server
      const result = await apiSwitchWallet(wallet.xrpl_address);

      // Store the new token
      storeWalletToken(wallet.xrpl_address, result.token, {
        ...user!,
        xrpl_address: wallet.xrpl_address,
      });

      // Switch to the wallet
      switchToWallet(wallet.xrpl_address);

      showSuccess(`Switched to ${wallet.nickname || formatAddress(wallet.xrpl_address)}`);
      setIsOpen(false);

      // Reload the page to refresh all data
      window.location.reload();
    } catch (err: any) {
      console.error('Failed to switch wallet:', err);
      showError(err.message || 'Failed to switch wallet');
      // Re-enable 401 handling on error (user stays on page)
      setSuppressLogoutOn401(false);
    } finally {
      setIsSwitching(false);
    }
  }, [user, switchToWallet, storeWalletToken, showSuccess, showError]);

  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);

  const handleCopyWalletAddress = useCallback((address: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(null), 2000);
  }, []);

  const handleConnectClick = () => {
    setIsOpen(false);
    onConnectWallet();
  };

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.top - 8, // Position above the button with small gap
        left: rect.left,
      });
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative">
      {/* Three-dot menu button */}
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="p-1 rounded hover:bg-neutral-100 transition-colors"
        aria-label="Wallet menu"
        aria-expanded={isOpen}
      >
        <FeatherMoreHorizontal className="w-4 h-4 text-subtext-color" />
      </button>

      {/* Dropdown menu - rendered via portal to escape overflow */}
      {isOpen && createPortal(
        <div
          ref={menuRef}
          className="fixed min-w-[180px] bg-default-background rounded-md border border-solid border-neutral-border shadow-lg z-[9999]"
          style={{ top: menuPosition.top, left: menuPosition.left, transform: 'translateY(-100%)' }}
        >
          {/* Connect another wallet */}
          <button
            onClick={handleConnectClick}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-default-font hover:bg-neutral-100 transition-colors rounded-t-md ${otherWallets.length === 0 ? 'rounded-b-md' : ''}`}
          >
            <FeatherLink className="w-4 h-4" />
            <span>Connect wallet</span>
          </button>

          {/* Switch wallet section */}
          <div className="border-t border-neutral-border">
            {/* Divider with label */}
            <div className="px-3 py-1.5">
              <span className="text-xs text-subtext-color font-medium">Switch wallet</span>
            </div>

            {otherWallets.length > 0 ? (
              /* List of other wallets */
              otherWallets.map((wallet, index) => (
                <div
                  key={wallet.xrpl_address}
                  className={`flex items-center ${index === otherWallets.length - 1 ? 'rounded-b-md' : ''}`}
                >
                  <button
                    onClick={(e) => handleCopyWalletAddress(wallet.xrpl_address, e)}
                    className="pl-3 py-2 hover:bg-neutral-100 transition-colors"
                    title="Copy address"
                  >
                    {copiedAddress === wallet.xrpl_address ? (
                      <FeatherCheck className="w-3.5 h-3.5 text-green-500" />
                    ) : (
                      <FeatherCopy className="w-3.5 h-3.5 text-subtext-color" />
                    )}
                  </button>
                  <button
                    onClick={() => handleSwitchWallet(wallet)}
                    disabled={isSwitching}
                    className="flex-1 flex items-center gap-2 px-2 pr-3 py-2 text-sm text-default-font hover:bg-neutral-100 transition-colors disabled:opacity-50 text-left"
                  >
                    <span>{wallet.nickname || formatAddress(wallet.xrpl_address)}</span>
                  </button>
                </div>
              ))
            ) : (
              /* No other wallets message */
              <div className="px-3 py-2 text-sm text-subtext-color rounded-b-md">
                No other wallets connected
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
